<template>
  <v-dialog
    v-model="formDialogOpen"
    :fullscreen="xs"
    persistent
    scrollable
    :width="xs ? undefined : formDialogWidth"
  >
    <VForm
      ref="formRef"
    >
      <v-card
        class="dialog-card mx-auto"
        :subtitle="formDialogInstructions"
        :title="formDialogTitle"
      >
        <v-card-text>
          <GenericFormLayout
            v-model="formData"
            :dense="formDialogDense"
            :errors="formErrors"
            :fields="formDialogFields"
            :rules="formRules"
          />

          <v-alert
            v-if="displayGeneralError"
            type="error"
          >
            {{ formErrors }}
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            :disabled="formSubmitting"
            @click="onCancelForm()"
          >
            Cancel
          </v-btn>

          <v-btn
            color="success"
            :loading="formSubmitting"
            type="submit"
            @click.prevent="onSubmitForm()"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </VForm>
  </v-dialog>
</template>

<script setup lang="ts">
import { VForm } from 'vuetify/components';

const { xs } = useDisplay();
const formRef = ref<InstanceType<typeof VForm> | null>(null);
const toastAlerts = useToastAlerts();
const formDialogStore = useFormDialogStore();
const {
  formDialogTitle,
  formDialogInstructions,
  formDialogWidth,
  formDialogDense,
  formDialogFields,
  formDialogInitialData,
  formDialogOpen,
} = storeToRefs(formDialogStore);
const { setFormDialogResponse } = formDialogStore;

const formSubmitting = ref(false);
const formData = ref<GenericFormData>({});
const formRules = ref<FormRules>({});
const formErrors = ref<FormErrors>({});

watch(formDialogOpen, dialogIsOpen => {
  if (dialogIsOpen) {
    // set up the form
    const { data, rules, errors } = useSetupFormFromFields({
      fields: formDialogFields.value,
      initialData: formDialogInitialData.value,
    });
    formData.value = data.value;
    formRules.value = rules.value;
    formErrors.value = errors.value;
  }
  else {
    // tear down the form
    formData.value = {};
    formRules.value = {};
    formErrors.value = {};
  }
});

/**
 * Will be true if the error is not an object, or if it is an object but
 * doesn't contain any keys that match the field keys.
 */
const displayGeneralError = computed(() => {
  if (typeof formErrors.value !== 'object') {
    return true;
  }
  const errorKeys = Object.keys(formErrors.value);
  const fieldKeys = formDialogFields.value.map(field => field.id);
  return errorKeys.length && !fieldKeys.some(key => errorKeys.includes(key));
});

/** Closes the form dialog without submitting any data */
const onCancelForm = () => setFormDialogResponse(null);

/** Submits the existing form data and closes the form */
const onSubmitForm = async () => {
  // If already submitting, break
  if (formSubmitting.value) {
    return;
  }
  // If the form isn't valid, break
  if (!(await formRef.value?.validate())?.valid) {
    toastAlerts.error('Please address the above issues and resubmit.');
    return;
  }

  formSubmitting.value = true;
  formErrors.value = {}; // reset errors
  try {
    await setFormDialogResponse(formData.value);
  }
  catch (errors) {
    formErrors.value = errors as FormErrors;
  }
  finally {
    formSubmitting.value = false;
  }
};
</script>

<style scoped>
.dialog-card {
  width: 100%;
  max-width: 500px;
}
</style>
